import React, { useState } from "react";
import {
  Card,
  DatePicker,
  Space,
  Button,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Input,
  Select,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import {
  useReportsServiceDownloadStockMovementReport,
  useReportsServiceGetStockMovementReport,
} from "api/queries";

const { Title } = Typography;

const PERIOD_OPTIONS = [
  { label: "Today", value: "Today" },
  { label: "This Week", value: "This Week" },
  { label: "This Month", value: "This Month" },
  { label: "This Year", value: "This Year" },
];

const StockMovementReport = () => {
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));
  const [periodFilter, setPeriodFilter] = useState("This Month");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);

  const offset = (page - 1) * limit;

  const { data, isLoading } = useReportsServiceGetStockMovementReport({
    limit,
    offset,
    startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    search,
  });

  const { mutate: downloadReport, isPending: isDownloadPending } =
    useReportsServiceDownloadStockMovementReport();

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date: string) => dayjs(date).format("MMM D, YYYY"),
      sorter: (a: any, b: any) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    },
    {
      title: "Product",
      dataIndex: "productName",
      key: "productName",
      sorter: (a: any, b: any) => a.productName?.localeCompare(b.productName),
    },
    {
      title: "SKU",
      dataIndex: "productSku",
      key: "productSku",
    },
    {
      title: "Category",
      dataIndex: "productCategoryName",
      key: "productCategoryName",
    },
    {
      title: "Subcategory",
      dataIndex: "productSubcategoryName",
      key: "productSubcategoryName",
    },
    {
      title: "Brand",
      dataIndex: "productBrand",
      key: "productBrand",
    },
    {
      title: "Type",
      dataIndex: "movementType",
      key: "movementType",
      render: (type: string) => {
        const color =
          type === "incoming" ? "green" : type === "outgoing" ? "red" : "blue";
        return (
          <Tag color={color}>
            {type?.charAt(0).toUpperCase() + type?.slice(1)}
          </Tag>
        );
      },
      filters: [
        { text: "Incoming", value: "incoming" },
        { text: "Outgoing", value: "outgoing" },
        { text: "Adjustment", value: "adjustment" },
      ],
      onFilter: (value: any, record: any) => record.movementType === value,
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      render: (qty: number) => (
        <span style={{ color: qty >= 0 ? "#3f8600" : "#cf1322" }}>
          {qty >= 0 ? "+" : ""}
          {qty}
        </span>
      ),
      sorter: (a: any, b: any) => a.quantity - b.quantity,
    },
    {
      title: "Warehouse",
      dataIndex: "locationName",
      key: "locationName",
    },
    {
      title: "Invoice #",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
    },
    {
      title: "PO #",
      dataIndex: "purchaseOrderNumber",
      key: "purchaseOrderNumber",
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
    },
  ];

  // Period filter logic
  const handlePeriodChange = (value: string) => {
    setPeriodFilter(value);
    const now = dayjs();
    switch (value) {
      case "Today":
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
        break;
      case "This Week":
        setStartDate(now.startOf("week"));
        setEndDate(now.endOf("week"));
        break;
      case "This Month":
        setStartDate(now.startOf("month"));
        setEndDate(now.endOf("month"));
        break;
      case "This Year":
        setStartDate(now.startOf("year"));
        setEndDate(now.endOf("year"));
        break;
      default:
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
    }
  };

  const handleStartDateChange = (date: any) => {
    setStartDate(date || dayjs());
    setPeriodFilter("Custom");
  };
  const handleEndDateChange = (date: any) => {
    setEndDate(date || dayjs());
    setPeriodFilter("Custom");
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleTableChange = (pagination: any) => {
    if (pagination && pagination.current && pagination.pageSize) {
      setPage(pagination.current || 1);
      setLimit(pagination.pageSize || 100);
    }
  };

  const handleDownload = () => {
    downloadReport(
      {
        startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      },
      {
        onSuccess: (response: any) => {
          if (response?.downloadUrl) {
            window.open(response.downloadUrl, "_blank");
          }
        },
      }
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, delay: 0 }}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Title level={2}>Stock Movement Report</Title>
          <Space>
            <Input.Search
              value={search}
              onChange={handleSearchChange}
              placeholder="Search"
              style={{ width: 200 }}
              allowClear
            />
            <Select
              value={periodFilter}
              onChange={handlePeriodChange}
              style={{ width: 120 }}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
            <DatePicker
              value={startDate}
              onChange={handleStartDateChange}
              style={{ width: 120 }}
              placeholder="From"
            />
            <DatePicker
              value={endDate}
              onChange={handleEndDateChange}
              style={{ width: 120 }}
              placeholder="To"
            />
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={isDownloadPending}
              onClick={handleDownload}
            >
              Download Report
            </Button>
          </Space>
        </Space>

        {/* Summary Statistics */}
        <Row gutter={16}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Movements"
                value={data?.kpis?.totalMovements}
                loading={isLoading}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Incoming Records"
                value={data?.kpis?.totalIncoming}
                loading={isLoading}
                valueStyle={{ color: "#3f8600" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Outgoing Records"
                value={data?.kpis?.totalOutgoing}
                loading={isLoading}
                valueStyle={{ color: "#cf1322" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Incoming Stock"
                value={data?.kpis?.totalQuantityIncoming}
                loading={isLoading}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Outgoing Stock"
                value={data?.kpis?.totalQuantityOutgoing}
                loading={isLoading}
                valueStyle={{ color: "#faad14" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Main Data Table */}
        <Table
          columns={columns}
          dataSource={data?.items || []}
          pagination={{
            current: data?.offset !== undefined ? Math.floor(data.offset / (data.limit || limit)) + 1 : page,
            pageSize: data?.limit || limit,
            total: data?.total || 0,
            onChange: (page, pageSize) => {
              setPage(page);
              setLimit(pageSize);
            },
            pageSizeOptions: [10, 20, 50, 100],
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          loading={isLoading}
          scroll={{ x: "max-content" }}
          rowKey={(item) => item.idInventoryTransaction}
          onChange={handleTableChange}
          summary={(pageData) => {
            const totalIncoming = pageData.reduce(
              (sum, item) => sum + (item.quantity > 0 ? item.quantity : 0),
              0
            );
            const totalOutgoing = pageData.reduce(
              (sum, item) =>
                sum + (item.quantity < 0 ? Math.abs(item.quantity) : 0),
              0
            );
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={8}>
                  <strong>Total Movement</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8}>
                  <strong>
                    In: +{totalIncoming} / Out: -{totalOutgoing}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={9} colSpan={4} />
              </Table.Summary.Row>
            );
          }}
        />
      </Space>
    </motion.div>
  );
};

export default StockMovementReport;
